import { Injectable, computed, inject, signal } from '@angular/core';

// D3
import { ascending, quantile } from 'd3-array';
import { scaleLinear, scaleOrdinal, scaleThreshold } from 'd3-scale';
import { schemePaired } from 'd3-scale-chromatic';

// Lodash
import filter from 'lodash/filter';
import uniq from 'lodash/uniq';

// 23 Utils
import { deepValue } from '@23degrees/utilities/object.utils';

// 23 Models
import type { Border, FontFormat, HTMLSpacing, LineHeight, Shadow } from '@23degrees/shared-code/models';
import { BreakPointManager } from '@23degrees/shared/manager/breakpoint/breakpoint.manager';
import type { Color } from '@23degrees/shared/models/color.model';
import { DOCUMENT } from '@angular/common';
import chroma from 'chroma-js';
import { StyleObject } from './styles.model';

/**
 * The StyleService manages the MapBoxGl Styles
 * for the Map and Layers, it uses the DataService
 * to create an apropiate Style and serve it to the
 * MapService
 * This is a standalone helper function class
 * Do not modify existing functions -> make new functions
 * Cause as always helpers are used everywhere !!!!!
 */
@Injectable({ providedIn: 'root' })
export class StyleService {
  private document = inject(DOCUMENT);
  private breakPointManager = inject(BreakPointManager);

  private content: any;

  private stylesMap: Map<any, HTMLStyleElement> = new Map();
  private host: Node = this.document.head;

  public sizeFactor = computed(() => {
    if (this.content) {
      return this.breakPointManager.getSizeFactorSignal(this.content.slug)();
    }
    return 1;
  });

  /**
   * Generate CSS valid text shadow style out of TypeSpecific TextShadow
   *
   * @param {Shadow} shadow The format shadow object
   */
  public convertShadow(shadow: Shadow): string {
    if (!shadow) return '';
    return `${shadow.x}px ${shadow.y}px ${shadow.blur}px ${shadow.color}`;
  }

  /**
   * Generate CSS valid text shadow style out of TypeSpecific TextShadow
   *
   * @param {Shadow} shadow The format shadow object
   */

  public convertContour(contour: { size: number; color: string; blur: number }): string {
    if (!contour) return '';
    const multiShadow = false;
    if (!multiShadow) {
      return this.createTextShadow(contour.size, contour.color, contour.blur);
    }

    const shadows = [];
    for (let i = 1; i <= contour.size; i++) {
      shadows.push(this.createTextShadow(i, contour.color, contour.blur));
    }

    return shadows.join(', ');
  }

  createTextShadow(size: number, color: string, blur = 0) {
    return `-${size}px -${size}px ${blur}px ${color}, -${size}px ${size}px ${blur}px ${color}, ${size}px -${size}px ${blur}px ${color}, ${size}px ${size}px ${blur}px ${color}`;
  }

  /**
   * Generate CSS valid border style out of TypeSpecific Border
   * This will return an object instead of an string like other converter functions here do
   *
   * @param {Border} border The format shadow object
   */
  public convertBorder(border: Border): object {
    if (!border) return;
    return {
      borderStyle: border.type,
      borderColor: border.color,
      borderWidth: `${border.size.top}px ${border.size.right}px ${border.size.bottom}px ${border.size.left}px`,
    };
  }

  public convertLineHeight(lineHeight: LineHeight | string): string {
    if (!lineHeight) return 'initial';
    if (typeof lineHeight === 'string') {
      return lineHeight;
    }

    // if (lineHeight.value === 'initial') return 'initial';

    return `${lineHeight.value}`;
    // return `${lineHeight.value}${lineHeight.unit}`;
  }

  /**
   * Generate CSS valid spacing (margin/padding) value out of TypeSpecific HTMLSpacing
   */
  public convertSpacing(spacing: HTMLSpacing, ignoreScaling: boolean = false): string {
    if (!spacing) return '';
    let sizeFactor = 1;
    if (this.content && !ignoreScaling) {
      // first check is for report.. no layout there
      if (this.content.typeSpecific.layout && !this.content.typeSpecific.layout.oldLayoutStyle) {
        sizeFactor = this.breakPointManager.getSizeFactor(this.content.slug);
      }
    }

    const top = spacing.top * (spacing.topUnit !== '%' ? sizeFactor : 1) + spacing.topUnit;
    const right = spacing.right * (spacing.rightUnit !== '%' ? sizeFactor : 1) + spacing.rightUnit;
    const bottom = spacing.bottom * (spacing.bottomUnit !== '%' ? sizeFactor : 1) + spacing.bottomUnit;
    const left = spacing.left * (spacing.leftUnit !== '%' ? sizeFactor : 1) + spacing.leftUnit;

    return `${top} ${right} ${bottom} ${left}`;
  }

  public getHorizontalSpacing(spacing: HTMLSpacing, ignoreScaling: boolean = false) {
    let sizeFactor = 1;
    if (this.content && !ignoreScaling) {
      // first check is for report.. no layout there
      if (this.content.typeSpecific.layout && !this.content.typeSpecific.layout.oldLayoutStyle) {
        sizeFactor = this.breakPointManager.getSizeFactor(this.content.slug);
      }
    }

    const right = spacing.right * (spacing.rightUnit !== '%' ? sizeFactor : 1) + spacing.rightUnit;
    const left = spacing.left * (spacing.leftUnit !== '%' ? sizeFactor : 1) + spacing.leftUnit;

    return { left, right };
  }

  private convertFormatProperty(property: string, unit: string, fontFormat, sizeFactor: number = 1): object {
    const fontStyle = {};
    switch (property) {
      case 'size':
        fontStyle['font-size'] = fontFormat.size * sizeFactor + (unit || '');
        break;
      case 'lineheight':
        fontStyle['line-height'] = this.convertLineHeight(fontFormat.lineheight);
        break;
      default:
        fontStyle[property] = fontFormat[property];
        break;
    }
    return fontStyle;
  }

  public setContent = (content) => {
    this.content = content;
  };

  /**
   * Convert the type specific FontFormat to ng valid CSS Style
   *
   * @param {FontFormat} fontFormat
   * @param {string} prop Get a specific property of the font format
   * @param {string} unit The unit add as suffix to the property
   */
  public convertFontFormat(fontFormat: FontFormat, prop?: string, unit: string = '', ignoreScaling: boolean = false, background?) {
    if (!fontFormat) return {};
    const sizeFactor = this.content && !ignoreScaling ? this.breakPointManager.getSizeFactor(this.content.slug) : 1;

    if (prop) {
      return this.convertFormatProperty(prop, unit, fontFormat, sizeFactor);
    }

    if (fontFormat.weight && typeof fontFormat.weight !== 'string') {
      console.log('!!! styleService font:', typeof fontFormat.weight, fontFormat);
    }

    const styleObj: any = {
      fontSize: `${fontFormat.size * sizeFactor}px`,
      fontFamily: fontFormat.fontFamily,
      letterSpacing: `${fontFormat.letterSpacing || 0}px`,
      fontWeight: fontFormat.weight ? String(fontFormat.weight).replace('i', '') : 'normal',
      textAlign: fontFormat.position === 'auto' ? 'center' : fontFormat.position,
      color: fontFormat.color,
      lineHeight: this.convertLineHeight(fontFormat.lineheight),
      textShadow: this.convertShadow(fontFormat.shadow),
      margin: this.convertSpacing(fontFormat.margin),
      padding: this.convertSpacing(fontFormat.padding),
      fontStyle: fontFormat.fontStyle || 'normal',
    };

    if (background) {
      styleObj.background = background;
    }

    return styleObj;
  }

  private convertUnit = (area: any, side: string, sizeFactor: number = 1, emSize: number = 16) => {
    let size = area[side];
    if (area[`${side}Unit`] === 'em') size *= emSize; // 16
    if (area[`${side}Unit`] === 'rem') size *= 16;
    return size * sizeFactor;
  };

  public getBoxHeight = (fontFormat: FontFormat, border?: Border) => {
    if (!fontFormat) return 0;
    const sizeFactor = this.content ? this.breakPointManager.getSizeFactor(this.content.slug) : 1;
    const fontSize = fontFormat.size * sizeFactor;

    const marginTop = this.convertUnit(fontFormat.margin, 'top', sizeFactor, fontSize);
    const marginBottom = this.convertUnit(fontFormat.margin, 'bottom', sizeFactor, fontSize);

    const paddingTop = this.convertUnit(fontFormat.padding, 'top', sizeFactor, fontSize);
    const paddingBottom = this.convertUnit(fontFormat.padding, 'bottom', sizeFactor, fontSize);

    const borderTop = border && border.type !== 'none' ? border.size.top : 0;
    const borderBottom = border && border.type !== 'none' ? border.size.bottom : 0;

    return marginTop + marginBottom + paddingTop + paddingBottom + borderTop + borderBottom;
  };

  public getFormatHeight = (fontFormat: FontFormat, border?: Border) => {
    if (!fontFormat) return 0;
    const sizeFactor = this.content ? this.breakPointManager.getSizeFactor(this.content.slug) : 1;
    const fontSize = fontFormat.size * sizeFactor;

    let lineHeightValue = 1.25;
    if (fontFormat.lineheight.value !== 'initial') {
      lineHeightValue = Number.parseFloat(String(fontFormat.lineheight.value));
    }

    const lineHeight = fontFormat.lineheight.unit === 'em' ? lineHeightValue * fontSize : lineHeightValue;

    const boxHeight = this.getBoxHeight(fontFormat, border);

    return Math.max(fontSize, lineHeight) + boxHeight;
  };

  /**
   * Get the colors of the vmd style object
   * An array with with color property or a nested match array
   * @param colors
   */
  getStyleColors(colors): Array<Color> {
    if (Array.isArray(colors)) {
      return colors;
    }
    if (Array.isArray(deepValue(colors, 'match'))) {
      return colors.match;
    }
    if (typeof deepValue(colors, 'color') === 'string') {
      return [{ color: colors.color }];
    }
    if (typeof deepValue(colors, 'default.color') === 'string') {
      return [{ color: deepValue(colors, 'default.color') }];
    }
  }

  getColorType(colors): string {
    if (Array.isArray(colors)) {
      return 'percentage';
    }
    if (deepValue(colors, 'type')) {
      return deepValue(colors, 'type');
    }
    return '';
  }

  /**
   * Generate a set of colors
   *
   * THIS IS USED ON EXTERN COMPONENT: loading-indicator
   *
   * @param count How much colors to generate
   * @param colors A given set of colors
   */
  generateColors(count: number = 5, colors?): Array<string> {
    if (colors) {
      // if(typeof colors == "string") return chroma.scale(colors).mode('lab').colors(count);
      const items = this.getStyleColors(colors);
      if (!items) return chroma.scale(['yellow', 'red', 'black']).mode('lab').colors(count);
      if (items.length < 2) {
        return chroma.scale(['black', 'grey']).mode('lab').colors(count);
      }
      if (items.length === 1) {
        return chroma.scale(['grey', items[0].color]).mode('lab').colors(count);
      }
      return chroma
        .scale(items.map((entry) => entry.color))
        .mode('lab')
        .colors(count);
    }
    return chroma.scale(['black', 'grey']).mode('lab').colors(count);
  }

  /**
   * Get a color scale function for the given type
   * @param colors
   * @param data
   * @param valueField
   */
  createColorScale(colors, data?, valueField = 'value', range?) {
    let colorScale: any;
    if (Array.isArray(colors)) {
      colorScale = this._createExponentialColors(colors, data, valueField, range);
    } else if (deepValue(colors, 'type')) {
      switch (colors.type) {
        case 'categorical':
          colorScale = this._createCategoricalValue(colors);
          break;
        case 'percentage':
          colorScale = this._createExponentialColors(colors, data, valueField, range);
          break;
        case 'interval':
          colorScale = this._createIntervalColors(colors, data, valueField);
          break;
        case 'static':
          colorScale = (item: any) => colors.color;
          break;
        default:
          break;
      }
    } else {
      colorScale = scaleOrdinal(schemePaired);
    }
    return colorScale;
  }

  _createIntervalColors(colors, data, valueField) {
    const colorScale = scaleThreshold()
      .domain(colors.match.map((item) => item.value))
      .range(colors.match.map((item) => item.color));

    return (d) => colorScale(d.value || d);
  }

  _createCategoricalValue(colors, property: string = 'color') {
    return (item) => {
      const matches = deepValue(colors, 'match');
      if (!Array.isArray(matches)) {
        return colors.default.color || colors.default;
      }
      if (!matches.length) {
        return colors.default.color || colors.default;
      }
      const match = matches.find((c) => {
        if (Array.isArray(colors.field)) {
          let m = false;
          colors.field.find((t) => {
            if (t[colors.field] === item) m = t.value;
          });
          return c.value === m;
        }
        return c.value === deepValue(item, colors.field);
      });
      if (match) {
        return match[property];
      }
      return colors.default.color || colors.default || '#ccc';
    };
  }

  _createExponentialColors(colors, data, valueField: string = 'value', range?) {
    let values = uniq(data.map((item) => deepValue(item, valueField)).sort(ascending));
    if (range) {
      values = values.filter((obj) => obj > range[0] && obj < range[1]);
      values = values.concat(range).sort(ascending);
    }

    colors = this.getStyleColors(colors);

    const colorScale = scaleLinear()
      .domain(this._createQuantilesDomain(values, colors))
      .range(colors.map((color) => color.color || color));
    return (d) => colorScale(d[valueField] === 0 ? 0 : d[valueField] || d);
  }

  _createQuantilesDomain(values, colors) {
    values = filter(values, (item) => item != null);
    const styleColors = this.getStyleColors(colors);

    // styleColors = styleColors.sort((a, b) => a.value - b.value);
    return styleColors.map((color, idx) => {
      // FIX COLORS THAT ARE GENERATED WRONG!!!
      // color.value = idx * (100 / (styleColors.length - 1));
      const stop = color.value || color.stop || 0;
      return quantile(values.sort(ascending), stop / 100);
    });
  }

  _parseStyles(style: string | StyleObject, idPrefix?: string) {
    if (typeof style === 'string') return style;
    return this.cssStringFromObject(style, idPrefix);
  }

  // Css STYLE TO DOM

  private createStyleNode(styles: string) {
    const styleEl = this.document.createElement('style');
    styleEl.innerHTML = styles;
    return styleEl;
  }

  addStyle(key: any, style: string | StyleObject, host?: HTMLElement, withIdPrefix?: boolean) {
    const styleEl = this.createStyleNode(this._parseStyles(style, withIdPrefix ? key : undefined));
    this.stylesMap.set(key, styleEl);
    (host ?? this.host).appendChild(styleEl);
    return styleEl;
  }

  updateStyle(key: any, style: string | StyleObject, host?: HTMLElement, withIdPrefix?: boolean) {
    const styleEl = this.stylesMap.get(key);
    if (styleEl) {
      styleEl.innerHTML = this._parseStyles(style, withIdPrefix ? key : undefined);
    } else {
      this.addStyle(key, style, host, withIdPrefix);
    }
    return styleEl;
  }

  removeStyle(key: any, element: HTMLElement): void {
    const styleEl = this.stylesMap.get(key);
    if (styleEl) {
      this.stylesMap.delete(key);

      if(element){
        element.removeChild(element)
      }
      else{
        this.host.removeChild(styleEl);
      }
    }
  }

  styleObjectToString(style) {
    return Object.entries(style)
      .map(([k, v]) => `${k.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()}:${v}`)
      .join(';');
  }

  public updateStyleSheet = (className, styleObj, merge = false) => {
    if (!this.document?.styleSheets?.length) return;
    const styleSheet = this.document.styleSheets[0]; // CSSStyleSheet
    //@ts-ignore TODO: NEXT Check css rules
    const sheetRules = Array.from<CSSStyleRule>(styleSheet.cssRules);
    const matchingRules = sheetRules.filter((obj) => obj.selectorText === className); // split by , then check?

    // sort my name length, shortest = best match
    // matchingRules.sort((a, b) => a.selectorText.length - b.selectorText.length);
    let styleRule = matchingRules[0];

    // create new
    if (!styleRule) {
      styleSheet.insertRule(`${className} {}`, 0);
      //@ts-ignore TODO: NEXT Check css rules
      styleRule = styleSheet.cssRules[0];
    }

    // preserve previous styles
    const prevStyle = {};
    if (merge) {
      Object.values(styleRule.style).forEach((key) => {
        prevStyle[key] = styleRule.style[key];
      });
    }

    // update rule
    Object.assign(styleRule.style, prevStyle, styleObj);
    for (const [key, value] of Object.entries<string>(styleObj)) {
      const isImportant = typeof value === 'string' && value?.includes('!important');
      if (isImportant) {
        const cssKey = key.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`);
        styleRule.style.setProperty(cssKey, value.replace(/\s?!important/g, ''), isImportant ? 'important' : undefined);
      }
    }
  };

  public deleteStyleSheets = (classFilter: string) => {
    const styleSheet = this.document.styleSheets[0]; // CSSStyleSheet
    //@ts-ignore TODO: NEXT Check css rules
    const sheetRules = Array.from<CSSStyleRule>(styleSheet.cssRules);
    const matchingRules = sheetRules.filter((obj) => obj.selectorText?.includes(classFilter)); // split by , then check?

    for (const rule of matchingRules) {
      styleSheet.deleteRule(sheetRules.indexOf(rule));
    }
  };

  public cssStringFromObject(styles: Record<string, Record<string, string>>, id?: string): string {
    let cssString = '';
    for (const [selector, properties] of Object.entries(styles)) {
      cssString += `${id ? `#${id} ` : ''}${selector} {`;
      for (const [property, value] of Object.entries(properties)) {
        if (value !== undefined && value !== null) {
          const cssKey = property.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`);
          cssString += `${cssKey}: ${value};`;
        }
      }
      cssString += '}';
    }
    return cssString;
  }
}
